import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as _ from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { FeatureFilterStreamService } from 'src/app/shared/common/current-features/feature-filter-stream.service';
import { LayersStreams } from 'src/app/shared/common/current-layers/layers-streams.service';
import { MapWorkspacesStoreService } from 'src/app/shared/common/current-map-workspaces/map-workspaces-store.service';
import { MenuService } from 'src/app/shared/common/layout/menu.service';
import { MapWorkspacePermissionType } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace-permission';

import { ProjectStreamService } from 'src/app/shared/common/current-project/project-stream.service';
import { MapWorkspace } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace';
import { WorkspaceType } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace.types';
import { ProjectTaskMigrationStatus } from 'src/app/shared/map-data-services/project/project';
import { MapMenuCode, MapMenuItem, mapMenuList } from './map-menu-list';

@Component({
    templateUrl: './menu-tabs.component.html',
    selector: 'menu-tabs'
})
export class MenuTabsComponent implements OnInit, OnDestroy {
    // expose ButtonType enum to template
    public ButtonType = ButtonType;
    private destroyed = new Subject<void>();

    private activeMenuCodes: MapMenuCode[] = [];

    // Mutually exclusive menus = if menu is selected, defined menu array will be deselected
    private mutuallyExclusiveMenus: { [key: string]: MapMenuCode[] } = {
        [MapMenuCode.LAYERS]: [MapMenuCode.TASKS, MapMenuCode.LEGACY_TASKS, MapMenuCode.SURVEY_JOBS],
        [MapMenuCode.LEGACY_TASKS]: [MapMenuCode.LAYERS, MapMenuCode.TASKS],
        [MapMenuCode.TASKS]: [
            MapMenuCode.LAYERS,
            MapMenuCode.LEGACY_TASKS,
            MapMenuCode.FILTERS,
            MapMenuCode.ADD_TO_TASK
        ],
        [MapMenuCode.SURVEY_JOBS]: [MapMenuCode.LAYERS]
    };

    public highlightFilter: boolean;
    private filteredMapMenuList: MapMenuItem[] = [];
    private currentWorkspace: MapWorkspace;

    // exposing enum to template
    public MapWorkspacePermissionType = MapWorkspacePermissionType;

    constructor(
        private route: ActivatedRoute,
        private menuService: MenuService,
        private featureFilterStream: FeatureFilterStreamService,
        private mapWorkspacesStore: MapWorkspacesStoreService,
        private layersStreams: LayersStreams,
        private projectStreamService: ProjectStreamService
    ) {}

    ngOnInit(): void {
        // Initialize menu with filtered list
        this.resetFilteredMenuList();

        // Re-initialize menu on workspace change
        this.mapWorkspacesStore.currentMapWorkspaceStream.pipe(takeUntil(this.destroyed)).subscribe(workspace => {
            this.currentWorkspace = workspace;

            // Reset and apply filters for new workspace
            this.resetFilteredMenuList();
            this.applyWorkspaceFilters();

            // Initialize menu with the filtered list
            this.initMenu();
        });
        // Re-initialize menu on edit layer opening
        this.layersStreams.currentEditLayerStream.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.initMenu();
        });
    }

    initMenu(): void {
        // Set default active menu based on workspace type
        this.currentWorkspace?.type === WorkspaceType.SURVEY
            ? this.menuService.setActiveMenus([MapMenuCode.SURVEY_JOBS])
            : this.menuService.setActiveMenus([MapMenuCode.LAYERS]);

        this.menuService.activeMenusStream.pipe(takeUntil(this.destroyed)).subscribe(activeMenus => {
            this.activeMenuCodes = activeMenus;

            this.filteredMapMenuList.forEach(menu => {
                menu.selected = this.activeMenuCodes.includes(menu.menuCode);
            });

            // Select task menu if AddToTask panel is open
            if (this.activeMenuCodes.includes(MapMenuCode.ADD_TO_TASK)) {
                this.filteredMapMenuList.find(m => m.menuCode === MapMenuCode.TASKS).selected = true;
            }
        });

        this.featureFilterStream.activeFilterStream.pipe(takeUntil(this.destroyed)).subscribe(filters => {
            this.highlightFilter = filters.hasActiveFilters();
        });
    }

    ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }

    public activateMenu(menu: MapMenuItem): void {
        menu.selected = !menu.selected;

        if (menu.menuCode === MapMenuCode.TASKS) {
            // Task panel closes all open menus. This allows revert to previous state when task panel is closed
            this.menuService.previousActiveMenusStream.next(_.cloneDeep(this.activeMenuCodes));
        }

        this.toggleMutuallyExclusiveMenus(menu);
        if (menu.selected) {
            this.activeMenuCodes.push(menu.menuCode);
        } else {
            this.activeMenuCodes.splice(this.activeMenuCodes.indexOf(menu.menuCode), 1);
        }

        this.menuService.activeMenusStream.next(this.activeMenuCodes);
    }

    // Mutually exclusive menus = if menu is selected, defined menu array will be deselected - see more in variable
    public toggleMutuallyExclusiveMenus(menu: MapMenuItem): void {
        if (this.mutuallyExclusiveMenus[menu.menuCode] && menu.selected) {
            this.mutuallyExclusiveMenus[menu.menuCode].forEach((menuCode: MapMenuCode) => {
                if (this.activeMenuCodes.indexOf(menuCode) > -1) {
                    let index = this.filteredMapMenuList.map(m => m.menuCode).indexOf(menuCode);
                    if (index > -1) {
                        this.filteredMapMenuList[index].selected = !menu.selected;
                    }
                    this.activeMenuCodes.splice(this.activeMenuCodes.indexOf(menuCode), 1);
                }
            });
        }
    }

    public getGroupMenuItems(group: number): MapMenuItem[] {
        return this.filteredMapMenuList.filter(m => m.group === group);
    }

    private resetFilteredMenuList(): void {
        // Make a deep clone to avoid modifying the original
        this.filteredMapMenuList = _.cloneDeep(mapMenuList);
    }

    private applyWorkspaceFilters(): void {
        if (!this.currentWorkspace) return;

        // Apply workspace-specific filters
        switch (this.currentWorkspace.type) {
            case WorkspaceType.GIS:
                this.removeMenuByCode(MapMenuCode.SURVEY_JOBS);

                // Apply task migration filters
                const migrationStatus = this.projectStreamService.taskMigrationStatusStream.getValue();
                if (migrationStatus === ProjectTaskMigrationStatus.COMPLETED) {
                    this.removeMenuByCode(MapMenuCode.LEGACY_TASKS);
                } else {
                    this.removeMenuByCode(MapMenuCode.TASKS);
                }
                break;

            case WorkspaceType.SURVEY:
                this.removeMenuByCode(MapMenuCode.LEGACY_TASKS);
                this.removeMenuByCode(MapMenuCode.TASKS);
                break;
        }
    }

    private removeMenuByCode(menuCode: MapMenuCode): void {
        const index = this.filteredMapMenuList.findIndex(m => m.menuCode === menuCode);
        if (index > -1) {
            this.filteredMapMenuList.splice(index, 1);
        }
    }
}
